/**
 * Payload and success schemas of the `v3.*` rpcs. The rpc groups themselves live in
 * `@fm/api` (they carry the api's auth middleware); everything a client, the MCP server or
 * the loop host needs to read or write them lives here.
 *
 * Instants on the wire are ISO-8601 strings. Optional fields are absent, never null.
 *
 * @since 1.0.0
 */
import { V3ProposalOrigin, V3ProposalPayload, V3ProposalStatus } from "@fm/db/models/V3ProposalFields";
import { Schema } from "effect";
import { DurationsProfile } from "./forecast.ts";
import { LoopKey } from "./keys.ts";
import { LoopDefinition, Params, RunEvent, RunFacts, RunReviewBody, RunStatus, StoredLoopDefinition } from "./model.ts";
import { RunPriority } from "./queue.ts";

/** A customer the caller belongs to; omitted means the caller's default customer. */
const customerId = Schema.optional(Schema.String);

/**
 * How the loops list can be sorted. Sorting and grouping happen in the client over the
 * list the rpc returns.
 *
 * @since 1.0.0
 */
export const LoopSort = Schema.Literal("name", "last_run", "runs", "median_time", "median_cost");
export type LoopSort = typeof LoopSort.Type;

/**
 * How the loops list can be grouped. A loop with several tags appears under each.
 *
 * @since 1.0.0
 */
export const LoopGroupBy = Schema.Literal("none", "tag", "author", "connector");
export type LoopGroupBy = typeof LoopGroupBy.Type;

/**
 * @since 1.0.0
 */
export const LoopRunStats = Schema.Struct({
  runs: Schema.Number,
  lastRun: Schema.optional(Schema.Struct({
    runId: Schema.String,
    number: Schema.Number,
    status: RunStatus,
    at: Schema.String,
  })),
  medianMinutes: Schema.optional(Schema.Number),
  medianUsd: Schema.optional(Schema.Number),
});
export type LoopRunStats = typeof LoopRunStats.Type;

/**
 * One row of the loops list.
 *
 * @since 1.0.0
 */
export const LoopSummary = Schema.Struct({
  id: Schema.String,
  slug: Schema.String,
  /** The loop's key (`SC`); absent until the api has given the loop one. */
  key: Schema.optional(LoopKey),
  name: Schema.String,
  description: Schema.String,
  tags: Schema.Array(Schema.String),
  author: Schema.Struct({ personId: Schema.String, name: Schema.optional(Schema.String) }),
  version: Schema.Number,
  createdAt: Schema.String,
  updatedAt: Schema.String,
  archived: Schema.Boolean,
  /** Connector ids the definition touches, first-mention order. */
  connectors: Schema.Array(Schema.String),
  stats: LoopRunStats,
});
export type LoopSummary = typeof LoopSummary.Type;

/**
 * @since 1.0.0
 */
export const LoopVersionSummary = Schema.Struct({
  version: Schema.Number,
  changeNote: Schema.optional(Schema.String),
  authorPersonId: Schema.String,
  createdAt: Schema.String,
});
export type LoopVersionSummary = typeof LoopVersionSummary.Type;

/**
 * A loop page: the summary, the current definition and its version history.
 *
 * @since 1.0.0
 */
export const LoopDetail = Schema.Struct({
  summary: LoopSummary,
  definition: StoredLoopDefinition,
  versions: Schema.Array(LoopVersionSummary),
});
export type LoopDetail = typeof LoopDetail.Type;

/**
 * `v3.loops.list`.
 *
 * @since 1.0.0
 */
export const LoopListInput = Schema.Struct({
  customerId,
  /** Loops carrying ALL of these tags. */
  tags: Schema.optional(Schema.Array(Schema.String)),
  q: Schema.optional(Schema.String),
  includeArchived: Schema.optional(Schema.Boolean),
});
export type LoopListInput = typeof LoopListInput.Type;

/**
 * `v3.loops.get` and `v3.loops.archive`.
 *
 * @since 1.0.0
 */
export const LoopRef = Schema.Struct({ customerId, loopId: Schema.String });
export type LoopRef = typeof LoopRef.Type;

/**
 * `v3.loops.define`: create a loop, or with `loopId` change one. A changed definition
 * appends a version; the definition is validated against the catalog first.
 *
 * @since 1.0.0
 */
export const LoopDefineInput = Schema.Struct({
  customerId,
  loopId: Schema.optional(Schema.String),
  name: Schema.String,
  /** Unique within the customer; ignored when `loopId` is given (a slug never changes). */
  slug: Schema.String,
  /** The loop's key, unique within the customer; absent means the api picks one. */
  key: Schema.optional(LoopKey),
  description: Schema.String,
  tags: Schema.Array(Schema.String),
  definition: StoredLoopDefinition,
  changeNote: Schema.optional(Schema.String),
  /**
   * The `reviewDigest` the preview returned for exactly this definition. Required for a flexible
   * definition; when given, it must match.
   */
  reviewDigest: Schema.optional(Schema.String),
});
export type LoopDefineInput = typeof LoopDefineInput.Type;

/**
 * One row of a runs list. `costUsd` is the sum of the run's `agent.finished.costUsd` —
 * the evaluator's included; `run.reviewed.costUsd` is display-only and never added.
 *
 * @since 1.0.0
 */
export const RunSummary = Schema.Struct({
  id: Schema.String,
  loopId: Schema.String,
  loopName: Schema.String,
  loopSlug: Schema.String,
  loopVersion: Schema.Number,
  number: Schema.Number,
  /** The run's key, `<loop key>-<number>` (`SC-52`); absent while its loop has no key. */
  key: Schema.optional(Schema.String),
  title: Schema.String,
  status: RunStatus,
  createdAt: Schema.String,
  startedAt: Schema.optional(Schema.String),
  endedAt: Schema.optional(Schema.String),
  costUsd: Schema.Number,
  createdByPersonId: Schema.String,
  resumedFromRunId: Schema.optional(Schema.String),
  /** The highest event seq recorded. */
  lastSeq: Schema.Number,
  /** "3 of 4 met": conditions holding when last checked, out of the definition's. */
  conditionsMet: Schema.Number,
  conditionsTotal: Schema.Number,
  /** Absent means 0 (no priority). */
  priority: Schema.optional(RunPriority),
  /** The manual queue rank; absent until the run is moved (then `createdAt` in ms places it). */
  queueRank: Schema.optional(Schema.Number),
});
export type RunSummary = typeof RunSummary.Type;

/**
 * A stored review: the measured facts and the evaluator's reading.
 *
 * @since 1.0.0
 */
export const RunReview = Schema.Struct({ facts: RunFacts, review: RunReviewBody });
export type RunReview = typeof RunReview.Type;

/**
 * A run page's first paint: the run, the definition version it ran, its inputs, every
 * event so far, its review when there is one, and the loop's learned step durations for
 * the forecast when the api has them. `v3.runs.watch` continues from `run.lastSeq`.
 *
 * @since 1.0.0
 */
export const RunDetail = Schema.Struct({
  run: RunSummary,
  definition: LoopDefinition,
  inputs: Params,
  events: Schema.Array(RunEvent),
  review: Schema.optional(RunReview),
  profile: Schema.optional(DurationsProfile),
});
export type RunDetail = typeof RunDetail.Type;

/**
 * `v3.runs.start`. `inputs` holds text inputs verbatim and connector inputs as their
 * reference (a Linear issue identifier); the host snapshots connector inputs at start.
 *
 * @since 1.0.0
 */
export const RunStartInput = Schema.Struct({
  customerId,
  loopId: Schema.String,
  inputs: Schema.Record({ key: Schema.String, value: Schema.String }),
  title: Schema.optional(Schema.String),
});
export type RunStartInput = typeof RunStartInput.Type;

/**
 * @since 1.0.0
 */
export const RunStartResult = Schema.Struct({
  runId: Schema.String,
  number: Schema.Number,
  /** The run's key (`SC-52`); absent while its loop has no key. */
  key: Schema.optional(Schema.String),
  /** The run page, relative to orcaloops.ai. */
  url: Schema.String,
});
export type RunStartResult = typeof RunStartResult.Type;

/**
 * The runs `v3.runs.list` can be narrowed to: `failed` runs, and `stuck` ones.
 *
 * @since 1.0.0
 */
export const RunListStatus = Schema.Literal("failed", "stuck");
export type RunListStatus = typeof RunListStatus.Type;

/**
 * `v3.runs.list`.
 *
 * @since 1.0.0
 */
export const RunListInput = Schema.Struct({
  customerId,
  loopId: Schema.optional(Schema.String),
  limit: Schema.optional(Schema.Number),
  /** The opaque `next` of the previous page. */
  before: Schema.optional(Schema.String),
  /**
   * Only these runs: `failed` — the run's status is `failed`; `stuck` — the run is `starting`
   * or `running` and has appended no event for the stuck threshold (`V3_RUN_STUCK_MINUTES`,
   * 30 minutes by default). A `queued` run waits its turn and is never stuck.
   */
  status: Schema.optional(RunListStatus),
});
export type RunListInput = typeof RunListInput.Type;

/**
 * @since 1.0.0
 */
export const RunPage = Schema.Struct({
  runs: Schema.Array(RunSummary),
  next: Schema.optional(Schema.String),
});
export type RunPage = typeof RunPage.Type;

/**
 * `v3.runs.get`, `v3.runs.stop` and `v3.runs.resume`.
 *
 * @since 1.0.0
 */
export const RunRef = Schema.Struct({ customerId, runId: Schema.String });
export type RunRef = typeof RunRef.Type;

/**
 * `v3.runs.rename`: give a run a new name. `runId` takes a run id or a run key (`SC-52`);
 * `title` is trimmed and holds 1–120 characters.
 *
 * @since 1.0.0
 */
export const RunRenameInput = Schema.Struct({
  customerId,
  runId: Schema.String,
  title: Schema.Trim.pipe(Schema.minLength(1), Schema.maxLength(120)),
});
export type RunRenameInput = typeof RunRenameInput.Type;

/**
 * Reprioritize a queued run: set its priority, or move it just before or just after
 * another queued run with one of `before`/`after` (a move takes that run's priority). A
 * request sets a priority or moves, never a mix. `runId`, `before` and `after` each take a
 * run id or a run key (`SC-52`).
 *
 * @since 1.0.0
 */
export const RunPrioritizeInput = Schema.Struct({
  customerId,
  runId: Schema.String,
  priority: Schema.optional(RunPriority),
  before: Schema.optional(Schema.String),
  after: Schema.optional(Schema.String),
});
export type RunPrioritizeInput = typeof RunPrioritizeInput.Type;

/**
 * Ask for the {@link WorkSnapshot} of a customer's work.
 *
 * @since 1.0.0
 */
export const WorkSnapshotInput = Schema.Struct({ customerId });
export type WorkSnapshotInput = typeof WorkSnapshotInput.Type;

/**
 * What the queue projection needs: `capacity`, the online loops runners, and a p50/p90
 * estimate per run in minutes — a queued run's whole length, a running run's time left —
 * from the loop's run history or the forecast.
 *
 * @since 1.0.0
 */
export const WorkSnapshot = Schema.Struct({
  capacity: Schema.Number,
  estimates: Schema.Array(Schema.Struct({
    runId: Schema.String,
    p50Minutes: Schema.Number,
    p90Minutes: Schema.Number,
    basis: Schema.Literal("loop_history", "forecast"),
  })),
});
export type WorkSnapshot = typeof WorkSnapshot.Type;

/**
 * `v3.runs.watch`: every event after `afterSeq`, first from storage and then live as the
 * host appends them. `run.finished` comes before `run.reviewed`; the stream ends once both
 * have arrived, or 15 minutes after `run.finished` when no review follows.
 *
 * @since 1.0.0
 */
export const RunWatchInput = Schema.Struct({ customerId, runId: Schema.String, afterSeq: Schema.Number });
export type RunWatchInput = typeof RunWatchInput.Type;

/**
 * What a resumed run starts from: the run it continues and the branches holding that
 * run's work on the workspace remote — its change branch and each kept agent's
 * `v3/<runId>/wip/<agentId>` ({@link wipBranchOf}), as its `run.finished` listed them.
 * `changeBranch` is the chain's change branch — the first run's `v3/<runId>/change`
 * ({@link changeBranchOf}) — which the resumed run keeps working on, so it adopts that
 * branch's pull request instead of opening another. Without it the resumed run gets its
 * own ({@link runChangeBranch}).
 *
 * @since 1.0.0
 */
export const ResumeFrom = Schema.Struct({
  runId: Schema.String,
  branches: Schema.Array(Schema.String),
  /** The change branch the resumed run keeps working on, and whose pull request it adopts. */
  changeBranch: Schema.optional(Schema.String),
});
export type ResumeFrom = typeof ResumeFrom.Type;

/**
 * `v3.host.spec`: everything the loop host needs to run, read once at start.
 *
 * @since 1.0.0
 */
export const HostSpec = Schema.Struct({
  run: RunSummary,
  customerId: Schema.String,
  definition: LoopDefinition,
  inputs: Params,
  resume: Schema.optional(ResumeFrom),
  /** The run page, absolute, for comments and change-request descriptions. */
  runUrl: Schema.String,
});
export type HostSpec = typeof HostSpec.Type;

/**
 * `v3.host.spec` takes only the run id: the host token already names the run.
 *
 * @since 1.0.0
 */
export const HostSpecInput = Schema.Struct({ runId: Schema.String });
export type HostSpecInput = typeof HostSpecInput.Type;

/**
 * `v3.host.append`: a batch of events in `seq` order. Appending a seq already stored is a
 * no-op, so the host retries a failed batch as is.
 *
 * @since 1.0.0
 */
export const HostAppendInput = Schema.Struct({ runId: Schema.String, events: Schema.Array(RunEvent) });
export type HostAppendInput = typeof HostAppendInput.Type;

/**
 * @since 1.0.0
 */
export const HostAppendResult = Schema.Struct({ lastSeq: Schema.Number });
export type HostAppendResult = typeof HostAppendResult.Type;

/** A proposal revision's digest. */
const proposalDigest = Schema.String.pipe(Schema.pattern(/^sha256:[0-9a-f]{64}$/));

/**
 * `v3.intake.propose`: a new proposal, or the next revision of one at the revision its person read.
 *
 * @since 1.0.0
 */
export const ProposeInput = Schema.Struct({
  customerId,
  loopId: Schema.String,
  /** The loop version the proposal is for; refused if the loop has moved on. */
  expectedLoopVersion: Schema.Number.pipe(Schema.int(), Schema.positive()),
  /** Revise this proposal… */
  proposalId: Schema.optional(Schema.String),
  /** …at this revision. */
  expectedRevision: Schema.optional(Schema.Number.pipe(Schema.int(), Schema.positive())),
  payload: V3ProposalPayload,
});
export type ProposeInput = typeof ProposeInput.Type;

/**
 * @since 1.0.0
 */
export const ProposalRef = Schema.Struct({ customerId, proposalId: Schema.String });
export type ProposalRef = typeof ProposalRef.Type;

/**
 * `v3.intake.approve`: its person approves one exact revision, by its digest, in their own signed-in
 * session. The digest proves which payload was shown; the session proves who agreed.
 *
 * @since 1.0.0
 */
export const ProposalApproveInput = Schema.Struct({
  customerId,
  proposalId: Schema.String,
  revision: Schema.Number.pipe(Schema.int(), Schema.positive()),
  digest: proposalDigest,
  note: Schema.optional(Schema.String.pipe(Schema.maxLength(600))),
});
export type ProposalApproveInput = typeof ProposalApproveInput.Type;

/**
 * @since 1.0.0
 */
export const ProposalWithdrawInput = Schema.Struct({
  customerId,
  proposalId: Schema.String,
  expectedRevision: Schema.Number.pipe(Schema.int(), Schema.positive()),
});
export type ProposalWithdrawInput = typeof ProposalWithdrawInput.Type;

/**
 * A proposal as its person reviews it: the whole current revision and its digest, what about it needs
 * approval and whether it has it, its revisions and approvals, and whether it can start. Every `at` is
 * an ISO-8601 instant.
 *
 * @since 1.0.0
 */
export const ProposalView = Schema.Struct({
  id: Schema.String,
  loopId: Schema.String,
  loopVersion: Schema.Number,
  status: V3ProposalStatus,
  ownerPersonId: Schema.String,
  /** The run started from it, once started. */
  runId: Schema.optional(Schema.String),
  revision: Schema.Number,
  digest: Schema.String,
  payload: V3ProposalPayload,
  approvalReasons: Schema.Array(Schema.String),
  /** Its person's approval of this revision, when given. */
  approved: Schema.optional(Schema.Struct({
    by: Schema.String,
    at: Schema.String,
    note: Schema.optional(Schema.String),
  })),
  /** Open, and either needing no approval or approved, for this revision. */
  startable: Schema.Boolean,
  revisions: Schema.Array(Schema.Struct({
    revision: Schema.Number,
    digest: Schema.String,
    requiresApproval: Schema.Boolean,
    origin: V3ProposalOrigin,
    by: Schema.String,
    at: Schema.String,
  })),
  approvals: Schema.Array(Schema.Struct({
    revision: Schema.Number,
    digest: Schema.String,
    by: Schema.String,
    at: Schema.String,
  })),
  /** Its review page, relative to orcaloops.ai. */
  url: Schema.String,
});
export type ProposalView = typeof ProposalView.Type;

/**
 * `v3.intake.start`: start a flexible loop from this exact revision of its person's proposal — its inputs and
 * refinements alone — refused unless the proposal is still open at that revision for the loop's current
 * version, its digest matches, and, when it needs one, its person approved it. Its own request, so an api that
 * knows nothing of proposals refuses it rather than starting plain work.
 *
 * @since 1.0.0
 */
export const IntakeStartInput = Schema.Struct({
  customerId,
  loopId: Schema.String,
  proposalId: Schema.String,
  revision: Schema.Number.pipe(Schema.int(), Schema.positive()),
  digest: proposalDigest,
  title: Schema.optional(Schema.String),
  /** Reuse for retries of exactly this launch; scoped to the customer, actor and loop. */
  idempotencyKey: Schema.optional(Schema.String.pipe(Schema.minLength(1), Schema.maxLength(128))),
  /** Refuse a fresh launch if the displayed loop policy has changed. */
  expectedLoopVersion: Schema.optional(Schema.Number.pipe(Schema.int(), Schema.positive())),
}).annotations({ parseOptions: { onExcessProperty: "error" } });
export type IntakeStartInput = typeof IntakeStartInput.Type;

/**
 * The run page path on orcaloops.ai.
 *
 * @since 1.0.0
 */
export const runPath = (runId: string): string => `/v3/runs/${runId}`;

/**
 * The loop page path on orcaloops.ai.
 *
 * @since 1.0.0
 */
export const loopPath = (loopId: string): string => `/v3/loops/${loopId}`;

/**
 * A run's change branch on the workspace remote.
 *
 * @since 1.0.0
 */
export const changeBranchOf = (runId: string): string => `v3/${runId}/change`;

/**
 * A run's change branch: its resume's `changeBranch`, else `changeBranchOf(runId)`.
 *
 * @since 1.0.0
 */
export const runChangeBranch = (runId: string, resume: ResumeFrom | undefined): string =>
  resume?.changeBranch ?? changeBranchOf(runId);

/**
 * Where an agent's unlanded work is kept on the workspace remote.
 *
 * @since 1.0.0
 */
export const wipBranchOf = (runId: string, agentId: string): string => `v3/${runId}/wip/${agentId}`;
